import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { CapabilityNotice } from "@/components/bse/BseUatForm";
import { useAdminBseCapability } from "@/components/BseCapabilityStatus";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Send, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ADDRESS_TYPE, AUTH_MODE, BANK_TYPE, COMM_MODE, CONTACT_TYPE, CORP_SECTOR, DATA_SOURCE, EXEMPTION_CODE, FFI, GENDER, GIIN, GIIN_EXEMPT_CATEGORY, HOLDING, IDCW, INCOME, KYC_TYPE, NATURE_RELATION, NFE, NFE_SUB_CATEGORY, NOM_AUTH, NOM_RELATION, OCC_TYPE, OCCUPATION, ONBOARDING, OWNER, PAN_EXEMPT, PEP, TAX_ID_TYPE, TAX_PROFILES, TOP_DOCUMENT_TYPES, UBO_TYPE, WEALTH_SOURCE, WHOSE } from "@/data/bseUccV203";
const boolOptions = [{
  value: "true",
  label: "Yes"
}, {
  value: "false",
  label: "No"
}];
const PAN_ID = [{
  value: "pan",
  label: "PAN"
}, {
  value: "pan_exempt_ref_no",
  label: "PAN-exempt reference"
}];
const NOMINEE_ID = [{
  value: "pan",
  label: "PAN"
}, {
  value: "passport",
  label: "Passport"
}, {
  value: "driving_license",
  label: "Driving licence"
}, {
  value: "aadhaar",
  label: "Aadhaar — last 4 digits only"
}];
const GUARDIAN_ID = [...NOMINEE_ID, {
  value: "pan_exempt_ref_no",
  label: "PAN-exempt reference — UCC module variant"
}];
const HOLDER_DOCS = [{ value: "nomination_form", label: "Nomination form" }, { value: "wet_signature", label: "Wet signature" }, { value: "aadhaar_esign", label: "Aadhaar eSign" }, { value: "elog", label: "Electronic log" }, { value: "supporting_document", label: "Supporting document" }, { value: "accredited_investor", label: "Accredited investor (SIF)" }];
const BANK_DOCS = [{
  value: "cancel_cheque",
  label: "Cancelled cheque"
}, {
  value: "bank_statement",
  label: "Bank statement"
}, {
  value: "bank_log",
  label: "Bank log"
}];
const emptyContact = () => ({
  contact_number: "",
  extension: "",
  country_code: "",
  whose_contact_number: "",
  email_address: "",
  whose_email_address: "",
  contact_type: "",
  fax_no: ""
});
const emptyAddress = () => ({
  address_line_1: "",
  address_line_2: "",
  address_line_3: "",
  city: "",
  state: "",
  country: "",
  postalcode: ""
});
const emptyIdentifier = (identifier_type = "") => ({
  identifier_type,
  identifier_number: "",
  issue_date: "",
  expiry_date: "",
  file_name: "",
  file_size: "",
  file_blob: ""
});
const emptyGuardian = () => ({
  first_name: "",
  middle_name: "",
  last_name: "",
  dob: "",
  is_pan_exempt: "",
  pan_exempt_category: "",
  identifier: [emptyIdentifier()]
});
const emptyNominee = () => ({
  person: {
    first_name: "",
    middle_name: "",
    last_name: "",
    dob: "",
    gender: ""
  },
  comm_addr: emptyAddress(),
  contact: emptyContact(),
  nomination_percent: "",
  nomination_relation: "",
  is_pan_exempt: false,
  identifier: [emptyIdentifier()],
  is_minor: "",
  guardian: emptyGuardian()
});
const emptyHolder = (rank = "1") => ({
  holder_rank: rank,
  occ_code: "",
  auth_mode: "",
  is_pan_exempt: "",
  pan_exempt_category: "",
  identifier: [emptyIdentifier()],
  kyc_type: "",
  ckyc_number: "",
  person: {
    first_name: "",
    middle_name: "",
    last_name: "",
    dob: "",
    gender: ""
  },
  contact: [emptyContact()],
  nomination: [],
  guardian_relation: ""
});
const emptyBank = () => ({
  ifsc_code: "",
  bank_acc_num: "",
  bank_acc_type: "",
  account_owner: "",
  identifier: []
});
const emptyDepository = () => ({
  depository_code: "",
  dp_id: "",
  client_id: "",
  cmbp_id: "",
  bank_account: "",
  account_owner: "",
  identifier: [],
  is_default: ""
});
const emptyTaxResidency = () => ({
  country: "",
  tax_id_no: "",
  tax_id_type: ""
});
const emptyUboDetail = () => ({
  person: {
    first_name: "",
    middle_name: "",
    last_name: "",
    dob: "",
    gender: "",
    tax_residency: emptyTaxResidency()
  },
  place_of_birth: "",
  country_of_birth: "",
  occ_code: "",
  occ_type: "",
  beneficiary_percent: "",
  ubo_type_code: "",
  addr: emptyAddress(),
  ubo_addr_type: "",
  contact: emptyContact(),
  identifier: [emptyIdentifier()],
  ubo_declaration_flag: "",
  exchange_name: "",
  isin: "",
  name_of_realted_listed_company: "",
  ubo_category: "",
  ubo_email_id: "",
  smo_designation: ""
});
const emptyFatca = (rank = "1") => ({
  holder_rank: rank,
  place_of_birth: "",
  country_of_birth: "",
  client_name: "",
  investor_type: "",
  dob: "",
  father_name: "",
  spouse_name: "",
  address_type: "",
  occ_code: "",
  occ_type: "",
  tax_status: "",
  exemption_code: "",
  identifier: emptyIdentifier(),
  corporate_service_sector: "",
  wealth_source: "",
  income_slab: "",
  net_worth: "",
  date_of_net_worth: "",
  politically_exposed: "",
  is_self_declared: "",
  data_source: "",
  log_name: "",
  tax_residency: [emptyTaxResidency()],
  ffi_drnfe: "",
  is_giin_avail: "",
  giin_no: "",
  giin_exempt_category: "",
  spr_name: "",
  nfe_category: "",
  nfe_sub_category: "",
  nature_of_business: "",
  nature_of_relation: "",
  ubo: {
    is_ubo_applicable: "",
    ubo_count: 1,
    ubo_detail: [emptyUboDetail()]
  },
  npo: {
    npo_form: "",
    npo_dcl: "",
    npo_rg_no: [""]
  }
});
function Field({
  label,
  hint,
  ...props
}) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label><Input className="h-9" {...props} />{hint && <p className="text-[10px] text-[var(--ink-muted)]">{hint}</p>}</div>;
}
function SelectField({
  label,
  options,
  hint,
  ...props
}) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label><select className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" {...props}><option value="">Select…</option>{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>{hint && <p className="text-[10px] text-[var(--ink-muted)]">{hint}</p>}</div>;
}
function Section({
  title,
  note,
  action,
  children
}) {
  return <section className="rounded-xl border border-[var(--glass-border)] p-4"><div className="mb-3 flex items-start justify-between gap-3"><div><h2 className="text-sm font-semibold text-[var(--brand)]">{title}</h2>{note && <p className="mt-1 text-xs text-[var(--ink-muted)]">{note}</p>}</div>{action}</div>{children}</section>;
}
function Grid({
  children,
  cols = "sm:grid-cols-2 lg:grid-cols-3"
}) {
  return <div className={`grid gap-3 ${cols}`}>{children}</div>;
}
function clean(value) {
  if (Array.isArray(value)) return value.map(clean).filter(item => item !== undefined);
  if (value && typeof value === "object") {
    const entries = Object.entries(value).map(([key, item]) => [key, clean(item)]).filter(([, item]) => item !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (value === "" || value === null || value === undefined) return undefined;
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
}
function updateAt(setter, index, transform) {
  setter(items => items.map((item, position) => position === index ? transform(item) : item));
}
async function loadDocument(file, onReady) {
  if (!file) return;
  const fileSize = Math.ceil(file.size / (1024 * 1024)) * 1024;
  if (fileSize > 10240) throw new Error("BSE accepts documents up to 10 MB");
  const reader = new FileReader();
  reader.onload = () => onReady({
    file_name: file.name,
    file_size: fileSize,
    file_blob: String(reader.result).split(",").pop()
  });
  reader.readAsDataURL(file);
}
function DocumentFields({
  value,
  onChange,
  types,
  label = "Document"
}) {
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  return <Grid><SelectField label={`${label} type`} value={value.identifier_type} options={types} onChange={set("identifier_type")} /><Field label="Document / reference number" value={value.identifier_number} onChange={set("identifier_number")} /><Field label="Issue date" type="date" value={value.issue_date} onChange={set("issue_date")} /><Field label="Expiry date" type="date" value={value.expiry_date} onChange={set("expiry_date")} /><div className="space-y-1.5"><Label className="text-xs">Upload file</Label><Input type="file" onChange={event => loadDocument(event.target.files?.[0], file => onChange({
        ...value,
        ...file
      })).catch(error => toast.error(error.message))} /><p className="text-[10px] text-[var(--ink-muted)]">{value.file_name ? `${value.file_name} · wire file_size ${value.file_size} KB` : "PDF/JPG/PNG etc.; AOF max 4 MB, other documents max 10 MB"}</p></div>
</Grid>
;
}
function ContactEditor({
  value,
  onChange,
  title = "Contact"
}) {
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  const landline = value.contact_type === "RE" || value.contact_type === "OF";
  return <div className="rounded-lg border p-3"><p className="mb-2 text-xs font-semibold">{title}</p><Grid><Field label="Mobile / contact number" value={value.contact_number} maxLength={15} onChange={set("contact_number")} /><Field label="Calling country code" value={value.country_code} placeholder="91 or +91" maxLength={3} onChange={set("country_code")} /><SelectField label="Whose mobile/contact is this?" value={value.whose_contact_number} options={WHOSE} onChange={set("whose_contact_number")} /><Field label="Email address" type="email" value={value.email_address} maxLength={50} onChange={set("email_address")} /><SelectField label="Whose email ID is this?" value={value.whose_email_address} options={WHOSE} onChange={set("whose_email_address")} /><SelectField label="Contact type" value={value.contact_type} options={CONTACT_TYPE} onChange={set("contact_type")} />{landline && <><Field label="Extension" value={value.extension} maxLength={15} onChange={set("extension")} /><Field label="Fax number" value={value.fax_no} maxLength={20} onChange={set("fax_no")} /></>}
</Grid>
</div>;
}
function AddressEditor({
  value,
  onChange,
  foreign = false
}) {
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  return <Grid><Field label="Address line 1" value={value.address_line_1} maxLength={foreign ? 40 : 120} onChange={set("address_line_1")} /><Field label="Address line 2" value={value.address_line_2} maxLength={foreign ? 40 : 120} onChange={set("address_line_2")} /><Field label="Address line 3" value={value.address_line_3} maxLength={foreign ? 40 : 120} onChange={set("address_line_3")} />{foreign && <><Field label="City" value={value.city} onChange={set("city")} /><Field label="State / province" value={value.state} onChange={set("state")} /><Field label="Country ISO-3" value={value.country} maxLength={3} placeholder="USA" onChange={set("country")} /></>}<Field label="Postal code" value={value.postalcode} onChange={set("postalcode")} />{!foreign && <><Field label="City (BSE derives from Indian PIN)" value={value.city} onChange={set("city")} /><Field label="State (BSE derives from Indian PIN)" value={value.state} onChange={set("state")} /><Field label="Country" value={value.country} onChange={set("country")} /></>}
</Grid>
;
}
function NomineeEditor({
  value,
  onChange,
  index,
  remove
}) {
  const person = key => event => onChange({
    ...value,
    person: {
      ...value.person,
      [key]: event.target.value
    }
  });
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  const guardian = value.guardian;
  return <div className="space-y-3 rounded-lg border p-3"><div className="flex justify-between"><p className="text-xs font-semibold">Nominee {index + 1}</p><Button type="button" size="sm" variant="ghost" onClick={remove}><Trash2 className="h-3.5 w-3.5" /></Button></div><Grid><Field label="First name" value={value.person.first_name} onChange={person("first_name")} /><Field label="Middle name" value={value.person.middle_name} onChange={person("middle_name")} /><Field label="Last name" value={value.person.last_name} onChange={person("last_name")} /><Field label="Date of birth (optional for nominee)" type="date" value={value.person.dob} onChange={person("dob")} /><SelectField label="Gender (optional)" value={value.person.gender} options={GENDER} onChange={person("gender")} /><Field label="Allocation %" type="number" min="0" max="100" value={value.nomination_percent} onChange={set("nomination_percent")} hint="Only 3 nominees may have non-zero shares; those shares must total 100. Nominees 4–5 must be 0." /><SelectField label="Relationship" value={value.nomination_relation} options={NOM_RELATION} onChange={set("nomination_relation")} /><SelectField label="Minor nominee?" value={value.is_minor} options={boolOptions} onChange={set("is_minor")} />
</Grid>
<ContactEditor title="Nominee contact (NA ownership is allowed)" value={value.contact} onChange={contact => onChange({
      ...value,
      contact
    })} /><div><p className="mb-2 text-xs font-semibold">Nominee address</p><AddressEditor foreign value={value.comm_addr} onChange={comm_addr => onChange({
        ...value,
        comm_addr
      })} /></div>
<DocumentFields label="Nominee ID" value={value.identifier[0]} types={NOMINEE_ID} onChange={identifier => onChange({
      ...value,
      identifier: [identifier]
    })} />{value.is_minor === "true" && <div className="space-y-3 rounded-lg bg-[var(--surface-2)] p-3"><p className="text-xs font-semibold">Minor nominee guardian</p><Grid><Field label="First name" value={guardian.first_name} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            first_name: event.target.value
          }
        })} /><Field label="Middle name" value={guardian.middle_name} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            middle_name: event.target.value
          }
        })} /><Field label="Last name" value={guardian.last_name} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            last_name: event.target.value
          }
        })} /><Field label="Date of birth" type="date" value={guardian.dob} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            dob: event.target.value
          }
        })} /><SelectField label="PAN exempt?" value={guardian.is_pan_exempt} options={boolOptions} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            is_pan_exempt: event.target.value
          }
        })} />{guardian.is_pan_exempt === "true" && <SelectField label="PAN exemption category" value={guardian.pan_exempt_category} options={PAN_EXEMPT} onChange={event => onChange({
          ...value,
          guardian: {
            ...guardian,
            pan_exempt_category: event.target.value
          }
        })} />}
</Grid>

<DocumentFields label="Guardian ID" value={guardian.identifier[0]} types={GUARDIAN_ID} onChange={identifier => onChange({
        ...value,
        guardian: {
          ...guardian,
          identifier: [identifier]
        }
      })} /></div>}</div>;
}
function HolderEditor({
  value,
  onChange,
  index,
  canRemove,
  remove,
  nominationEnabled
}) {
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  const person = key => event => onChange({
    ...value,
    person: {
      ...value.person,
      [key]: event.target.value
    }
  });
  const panExempt = value.is_pan_exempt === "true";
  return <div className="space-y-3 rounded-xl border p-4"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Badge variant="outline">rank {value.holder_rank}</Badge><p className="text-sm font-semibold">{value.holder_rank === "-1" ? "Minor’s guardian" : `Holder ${value.holder_rank}`}</p></div>{canRemove && <Button type="button" size="sm" variant="ghost" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}</div><Grid><SelectField label="Holder rank" value={value.holder_rank} options={[{
        value: "1",
        label: "1 — Primary"
      }, {
        value: "2",
        label: "2 — Second"
      }, {
        value: "3",
        label: "3 — Third"
      }, {
        value: "-1",
        label: "-1 — Guardian"
      }]} onChange={set("holder_rank")} /><SelectField label="Occupation" value={value.occ_code} options={OCCUPATION} onChange={set("occ_code")} /><SelectField label="Authentication mode" value={value.auth_mode} options={AUTH_MODE} onChange={set("auth_mode")} /><SelectField label="PAN exempt?" value={value.is_pan_exempt} options={boolOptions} onChange={event => {
        const exempt = event.target.value;
        onChange({
          ...value,
          is_pan_exempt: exempt,
          identifier: [emptyIdentifier(exempt === "true" ? "pan_exempt_ref_no" : "pan"), ...value.identifier.slice(1)]
        });
      }} />{panExempt && <SelectField label="PAN exemption category" value={value.pan_exempt_category} options={PAN_EXEMPT} onChange={set("pan_exempt_category")} />}<SelectField label="KYC type" value={value.kyc_type} options={KYC_TYPE} onChange={set("kyc_type")} />{value.kyc_type === "C" && <Field label="CKYC number" value={value.ckyc_number} minLength={14} maxLength={14} onChange={set("ckyc_number")} />}{value.holder_rank === "-1" && <SelectField label="Guardian relation" value={value.guardian_relation} options={[{
        value: "5",
        label: "5 — Father"
      }, {
        value: "12",
        label: "12 — Mother"
      }, {
        value: "23",
        label: "23 — Court-appointed legal guardian"
      }]} onChange={set("guardian_relation")} />}
</Grid>
<div><p className="mb-2 text-xs font-semibold">Person / entity</p><Grid><Field label="First / entity name" value={value.person.first_name} onChange={person("first_name")} /><Field label="Middle name" value={value.person.middle_name} onChange={person("middle_name")} /><Field label="Last name" value={value.person.last_name} onChange={person("last_name")} /><Field label="DOB / incorporation date" type="date" value={value.person.dob} onChange={person("dob")} /><SelectField label="Gender (individuals)" value={value.person.gender} options={GENDER} onChange={person("gender")} />
</Grid>
</div>
<DocumentFields label={panExempt ? "PAN-exempt reference" : "PAN"} value={value.identifier[0]} types={PAN_ID} onChange={identifier => onChange({
      ...value,
      identifier: [identifier, ...value.identifier.slice(1)]
    })} />
<Section title="Additional holder identifiers / documents" note="Nomination, signature, eSign, eLog, supporting and SIF accredited-investor documents use the same holder.identifier[] wire list." action={<Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...value, identifier: [...value.identifier, emptyIdentifier("")] })}><Plus className="mr-1 h-3.5 w-3.5" /> Holder document</Button>}>
  <div className="space-y-3">{value.identifier.slice(1).map((item, extraIndex) => <div key={extraIndex} className="relative rounded-lg border p-3"><DocumentFields label={`Holder document ${extraIndex + 1}`} value={item} types={HOLDER_DOCS} onChange={(identifier) => onChange({ ...value, identifier: value.identifier.map((entry, position) => position === extraIndex + 1 ? identifier : entry) })} /><Button type="button" size="sm" variant="ghost" className="absolute right-2 top-1" onClick={() => onChange({ ...value, identifier: value.identifier.filter((_, position) => position !== extraIndex + 1) })}><Trash2 className="h-3.5 w-3.5" /></Button></div>)}</div>
</Section>
<Section title="Contacts" note="Each holder has independent mobile/email ownership. NA is valid only for non-individuals or nominee contacts." action={<Button type="button" size="sm" variant="outline" onClick={() => onChange({
      ...value,
      contact: [...value.contact, emptyContact()]
    })}><Plus className="mr-1 h-3.5 w-3.5" /> Contact</Button>}><div className="space-y-3">{value.contact.map((contact, contactIndex) => <div key={contactIndex} className="relative"><ContactEditor title={`Contact ${contactIndex + 1}`} value={contact} onChange={next => onChange({
            ...value,
            contact: value.contact.map((item, position) => position === contactIndex ? next : item)
          })} />{value.contact.length > 1 && <Button type="button" size="sm" variant="ghost" className="absolute right-2 top-1" onClick={() => onChange({
            ...value,
            contact: value.contact.filter((_, position) => position !== contactIndex)
          })}><Trash2 className="h-3.5 w-3.5" /></Button>}</div>)}
</div></Section>
{value.holder_rank === "1" && nominationEnabled &&
<Section title="Nominees" note="Maximum 5; entered only under primary holder." action={value.nomination.length < 5 ? <Button type="button" size="sm" variant="outline" onClick={() => onChange({
      ...value,
      nomination: [...value.nomination, emptyNominee()]
    })}><Plus className="mr-1 h-3.5 w-3.5" /> Nominee</Button> : null}><div className="space-y-3">{value.nomination.map((nominee, nomineeIndex) => <NomineeEditor key={nomineeIndex} index={nomineeIndex} value={nominee} onChange={next => onChange({
          ...value,
          nomination: value.nomination.map((item, position) => position === nomineeIndex ? next : item)
        })} remove={() => onChange({
          ...value,
          nomination: value.nomination.filter((_, position) => position !== nomineeIndex)
        })} />)}</div></Section>
}</div>;
}
function TaxResidencyEditor({
  values,
  onChange
}) {
  return (
<Section title="Tax residencies" note="One to five per FATCA declaration." action={values.length < 5 ? <Button type="button" size="sm" variant="outline" onClick={() => onChange([...values, emptyTaxResidency()])}><Plus className="mr-1 h-3.5 w-3.5" /> Residency</Button> : null}><div className="space-y-2">{values.map((item, index) => <Grid key={index}><Field label={`Country ${index + 1} ISO-3`} value={item.country} maxLength={3} onChange={event => onChange(values.map((entry, position) => position === index ? {
          ...entry,
          country: event.target.value.toUpperCase()
        } : entry))} /><Field label="Tax ID number" value={item.tax_id_no} minLength={10} maxLength={50} onChange={event => onChange(values.map((entry, position) => position === index ? {
          ...entry,
          tax_id_no: event.target.value
        } : entry))} /><div className="flex items-end gap-2"><div className="flex-1"><SelectField label="Tax ID type" value={item.tax_id_type} options={TAX_ID_TYPE} onChange={event => onChange(values.map((entry, position) => position === index ? {
              ...entry,
              tax_id_type: event.target.value
            } : entry))} /></div>{values.length > 1 && <Button type="button" variant="ghost" onClick={() => onChange(values.filter((_, position) => position !== index))}><Trash2 className="h-4 w-4" /></Button>}</div>
</Grid>
)}</div></Section>
  );
}
function UboEditor({
  value,
  onChange
}) {
  const details = value.ubo_detail;
  return (
<Section title="Ultimate beneficial owners" note="Portal v2.0.3 normative ubo_detail shape; UBO count is derived from rows." action={<Button type="button" size="sm" variant="outline" onClick={() => onChange({
    ...value,
    ubo_count: details.length + 1,
    ubo_detail: [...details, emptyUboDetail()]
  })}><Plus className="mr-1 h-3.5 w-3.5" /> UBO</Button>}><SelectField label="Is UBO applicable?" value={value.is_ubo_applicable} options={boolOptions} onChange={event => onChange({
      ...value,
      is_ubo_applicable: event.target.value
    })} /><div className="mt-3 space-y-3">{details.map((item, index) => {
        const change = patch => onChange({
          ...value,
          ubo_count: details.length,
          ubo_detail: details.map((entry, position) => position === index ? {
            ...entry,
            ...patch
          } : entry)
        });
        return <div key={index} className="space-y-3 rounded-lg border p-3"><div className="flex justify-between"><p className="text-xs font-semibold">UBO {index + 1}</p>{details.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => {
              const next = details.filter((_, position) => position !== index);
              onChange({
                ...value,
                ubo_count: next.length,
                ubo_detail: next
              });
            }}><Trash2 className="h-3.5 w-3.5" /></Button>}</div><Grid><Field label="First name" value={item.person.first_name} onChange={event => change({
              person: {
                ...item.person,
                first_name: event.target.value
              }
            })} /><Field label="Middle name" value={item.person.middle_name} onChange={event => change({
              person: {
                ...item.person,
                middle_name: event.target.value
              }
            })} /><Field label="Last name" value={item.person.last_name} onChange={event => change({
              person: {
                ...item.person,
                last_name: event.target.value
              }
            })} /><Field label="Date of birth" type="date" value={item.person.dob} onChange={event => change({
              person: {
                ...item.person,
                dob: event.target.value
              }
            })} /><SelectField label="Gender" value={item.person.gender} options={GENDER} onChange={event => change({
              person: {
                ...item.person,
                gender: event.target.value
              }
            })} /><Field label="Place of birth" value={item.place_of_birth} onChange={event => change({
              place_of_birth: event.target.value
            })} /><Field label="Country of birth ISO-3" value={item.country_of_birth} maxLength={3} onChange={event => change({
              country_of_birth: event.target.value.toUpperCase()
            })} /><SelectField label="Occupation code" value={item.occ_code} options={OCCUPATION} onChange={event => change({
              occ_code: event.target.value
            })} /><SelectField label="Occupation type" value={item.occ_type} options={OCC_TYPE} onChange={event => change({
              occ_type: event.target.value
            })} /><SelectField label="UBO type" value={item.ubo_type_code} options={UBO_TYPE} onChange={event => change({
              ubo_type_code: event.target.value
            })} /><SelectField label="UBO address type" value={item.ubo_addr_type} options={ADDRESS_TYPE.slice(0, 5)} onChange={event => change({
              ubo_addr_type: event.target.value
            })} /><SelectField label="Category" value={item.ubo_category} options={[{
              value: "UBO",
              label: "UBO — Ultimate beneficial owner"
            }, {
              value: "SMO",
              label: "SMO — Senior managing official"
            }]} onChange={event => change({
              ubo_category: event.target.value
            })} /><Field label="Beneficiary %" type="number" min="0" max="100" value={item.beneficiary_percent} onChange={event => change({
              beneficiary_percent: event.target.value
            })} /><Field label="Email" type="email" value={item.ubo_email_id} onChange={event => change({
              ubo_email_id: event.target.value
            })} /><SelectField label="UBO declaration flag" value={item.ubo_declaration_flag} options={boolOptions} onChange={event => change({
              ubo_declaration_flag: event.target.value
            })} /><Field label="Exchange name (conditional)" value={item.exchange_name} maxLength={70} onChange={event => change({
              exchange_name: event.target.value
            })} /><Field label="ISIN (conditional, exact 12)" value={item.isin} maxLength={12} onChange={event => change({
              isin: event.target.value
            })} /><Field label="Related listed company" value={item.name_of_realted_listed_company} maxLength={70} onChange={event => change({
              name_of_realted_listed_company: event.target.value
            })} />{item.ubo_category === "SMO" && <Field label="SMO designation" value={item.smo_designation} minLength={5} maxLength={20} onChange={event => change({
              smo_designation: event.target.value
            })} />}
</Grid>
<Grid><Field label="Tax-residency country ISO-3" value={item.person.tax_residency.country} maxLength={3} onChange={event => change({
              person: {
                ...item.person,
                tax_residency: {
                  ...item.person.tax_residency,
                  country: event.target.value.toUpperCase()
                }
              }
            })} /><Field label="Tax ID" value={item.person.tax_residency.tax_id_no} onChange={event => change({
              person: {
                ...item.person,
                tax_residency: {
                  ...item.person.tax_residency,
                  tax_id_no: event.target.value
                }
              }
            })} /><SelectField label="Tax ID type" value={item.person.tax_residency.tax_id_type} options={TAX_ID_TYPE} onChange={event => change({
              person: {
                ...item.person,
                tax_residency: {
                  ...item.person.tax_residency,
                  tax_id_type: event.target.value
                }
              }
            })} />
</Grid>

<DocumentFields label="UBO identifier" value={item.identifier[0]} types={PAN_ID} onChange={identifier => change({
            identifier: [identifier]
          })} /><ContactEditor title="UBO contact (optional object)" value={item.contact} onChange={(contact) => change({ contact })} /><div><p className="mb-2 text-xs font-semibold">UBO address</p><AddressEditor foreign value={item.addr} onChange={addr => change({
              addr
            })} /></div></div>;
      })}</div></Section>
  );
}
function FatcaEditor({
  value,
  onChange,
  nonIndividual,
  npoRequired,
  index
}) {
  const set = key => event => onChange({
    ...value,
    [key]: event.target.value
  });
  return <div className="space-y-3 rounded-xl border p-4"><div className="flex items-center gap-2"><Badge variant="outline">holder {value.holder_rank}</Badge><p className="text-sm font-semibold">FATCA declaration {index + 1}</p></div><Grid><Field label="Client name — exact holder name" value={value.client_name} onChange={set("client_name")} /><Field label="DOB / incorporation date" type="date" value={value.dob} onChange={set("dob")} hint="Live UAT requires exact ISO date matching the holder, despite PDF MM-DD wording." /><Field label="Place of birth" value={value.place_of_birth} onChange={set("place_of_birth")} /><Field label="Country of birth ISO-3" value={value.country_of_birth} maxLength={3} onChange={event => onChange({
        ...value,
        country_of_birth: event.target.value.toUpperCase()
      })} /><SelectField label="Investor type" value={value.investor_type} options={[{
        value: "Individual",
        label: "Individual"
      }, {
        value: "Non-Individual",
        label: "Non-Individual"
      }]} onChange={set("investor_type")} /><Field label="FATCA tax status text" value={value.tax_status} onChange={set("tax_status")} /><Field label="Father name (conditional without PAN)" value={value.father_name} onChange={set("father_name")} /><Field label="Spouse name (conditional without PAN)" value={value.spouse_name} onChange={set("spouse_name")} /><SelectField label="Address type" value={value.address_type} options={ADDRESS_TYPE} onChange={set("address_type")} /><SelectField label="Occupation code" value={value.occ_code} options={OCCUPATION} onChange={set("occ_code")} /><SelectField label="Occupation type" value={value.occ_type} options={OCC_TYPE} onChange={set("occ_type")} /><SelectField label="Corporate service sector" value={value.corporate_service_sector} options={CORP_SECTOR} onChange={set("corporate_service_sector")} /><SelectField label="Source of wealth" value={value.wealth_source} options={WEALTH_SOURCE} onChange={set("wealth_source")} /><SelectField label="Income slab" value={value.income_slab} options={INCOME} onChange={set("income_slab")} /><SelectField label="Political exposure" value={value.politically_exposed} options={PEP} onChange={set("politically_exposed")} /><SelectField label="Self declared?" value={value.is_self_declared} options={boolOptions} onChange={set("is_self_declared")} /><SelectField label="Data source" value={value.data_source} options={DATA_SOURCE} onChange={set("data_source")} />{value.data_source === "E" && <Field label="Electronic log name" value={value.log_name} maxLength={64} onChange={set("log_name")} />}
</Grid>

<DocumentFields label="FATCA PAN / exempt reference" value={value.identifier} types={PAN_ID} onChange={identifier => onChange({
      ...value,
      identifier
    })} /><TaxResidencyEditor values={value.tax_residency} onChange={tax_residency => onChange({
      ...value,
      tax_residency
    })} />{nonIndividual && <>
<Section title="Non-individual FATCA classification"><Grid><SelectField label="Exemption code" value={value.exemption_code} options={EXEMPTION_CODE} onChange={set("exemption_code")} /><Field label="Net worth" type="number" min="100" value={value.net_worth} onChange={set("net_worth")} /><Field label="Net-worth date" type="date" value={value.date_of_net_worth} onChange={set("date_of_net_worth")} /><SelectField label="FFI / DRNFE" value={value.ffi_drnfe} options={FFI} onChange={set("ffi_drnfe")} /><SelectField label="GIIN availability" value={value.is_giin_avail} options={GIIN} onChange={set("is_giin_avail")} /><Field label="GIIN (exact 19)" value={value.giin_no} maxLength={19} onChange={set("giin_no")} /><SelectField label="GIIN exemption category" value={value.giin_exempt_category} options={GIIN_EXEMPT_CATEGORY} onChange={set("giin_exempt_category")} /><Field label="Sponsor name" value={value.spr_name} onChange={set("spr_name")} /><SelectField label="NFE category" value={value.nfe_category} options={NFE} onChange={set("nfe_category")} /><SelectField label="NFE sub-category" value={value.nfe_sub_category} options={NFE_SUB_CATEGORY} onChange={set("nfe_sub_category")} /><Field label="Nature of business" value={value.nature_of_business} onChange={set("nature_of_business")} /><SelectField label="Related-entity relation" value={value.nature_of_relation} options={NATURE_RELATION} onChange={set("nature_of_relation")} />
</Grid>
</Section>
<UboEditor value={value.ubo} onChange={ubo => onChange({
        ...value,
        ubo
      })} />{npoRequired &&
<Section title="NPO declaration" note="The published contract marks this block mandatory for every non-individual FATCA record and permits a list of registration numbers." action={<Button type="button" size="sm" variant="outline" onClick={() => onChange({
          ...value,
          npo: {
            ...value.npo,
            npo_rg_no: [...value.npo.npo_rg_no, ""]
          }
        })}><Plus className="mr-1 h-3.5 w-3.5" /> Registration number</Button>}><Grid><SelectField label="NPO form?" value={value.npo.npo_form} options={boolOptions} onChange={event => onChange({
            ...value,
            npo: {
              ...value.npo,
              npo_form: event.target.value
            }
          })} /><SelectField label="NPO declaration?" value={value.npo.npo_dcl} options={boolOptions} onChange={event => onChange({
            ...value,
            npo: {
              ...value.npo,
              npo_dcl: event.target.value
            }
          })} />
</Grid>
<div className="mt-3 space-y-2">{value.npo.npo_rg_no.map((number, index) => <div key={index} className="flex items-end gap-2"><div className="flex-1"><Field label={`NPO registration number ${index + 1}`} value={number} maxLength={50} onChange={event => onChange({
              ...value,
              npo: {
                ...value.npo,
                npo_rg_no: value.npo.npo_rg_no.map((item, position) => position === index ? event.target.value : item)
              }
            })} /></div>{value.npo.npo_rg_no.length > 1 && <Button type="button" variant="ghost" onClick={() => onChange({
              ...value,
              npo: {
                ...value.npo,
                npo_rg_no: value.npo.npo_rg_no.filter((_, position) => position !== index)
              }
            })}><Trash2 className="h-4 w-4" /></Button>}</div>)}</div>
</Section>
}</>}</div>;
}
export default function UccRegistrationV203() {
  const capability = useAdminBseCapability("ucc_registration");
  const [top, setTop] = useState({
    client_code: "",
    tax_code: "",
    holding_nature: "",
    rdmp_idcw_pay_mode: "",
    is_client_physical: "",
    is_client_demat: "",
    is_multi_ucc: "",
    parent_client_code: "",
    pms_client: "",
    pms_code: "",
    is_nomination_opted: "",
    nomination_auth_mode: "",
    nominee_soa: "",
    comm_mode: "",
    onboarding: "",
    is_aof_submitted: "",
    is_aof_ria_submitted: ""
  });
  const [commAddr, setCommAddr] = useState(emptyAddress());
  const [foreignAddr, setForeignAddr] = useState(emptyAddress());
  const [holders, setHolders] = useState([emptyHolder("1")]);
  const [banks, setBanks] = useState([emptyBank()]);
  const [depositories, setDepositories] = useState([]);
  const [fatca, setFatca] = useState([emptyFatca("1")]);
  const [includeDematFatca, setIncludeDematFatca] = useState(false);
  const [documents, setDocuments] = useState([emptyIdentifier()]);
  const [nominationApproval, setNominationApproval] = useState(emptyIdentifier(""));
  const [preview, setPreview] = useState(null);
  const [previewInput, setPreviewInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [taxProfiles, setTaxProfiles] = useState(TAX_PROFILES);
  const [contractMeta, setContractMeta] = useState({ version: "2.0.3", memberId: "" });
  useEffect(() => {
    let active = true;
    adminApi.get("/admin/bse2/ucc/registration-contract").then(({ data }) => {
      if (!active) return;
      const profiles = (data.tax_profiles || []).map(item => ({
        code: item.code,
        label: item.label,
        investorType: item.investor_type,
        resident: item.is_resident,
        minor: item.is_minor
      }));
      if (profiles.length) setTaxProfiles(profiles);
      setContractMeta({ version: data.version || "2.0.3", memberId: data.member_id || "" });
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  const profile = taxProfiles.find(item => item.code === top.tax_code);
  const physical = top.is_client_physical === "true";
  const demat = top.is_client_demat === "true";
  const includeFatca = physical || demat && includeDematFatca;
  const nominationEnabled = physical && top.is_nomination_opted === "true";
  const nonIndividual = profile?.investorType === "Non-Individual";
  const alignHolders = nextTop => {
    const nextProfile = taxProfiles.find(item => item.code === nextTop.tax_code);
    let next = holders.filter(holder => holder.holder_rank !== "-1");
    if (nextTop.holding_nature === "SI") next = [next[0] || emptyHolder("1")];
    if ((nextTop.holding_nature === "JO" || nextTop.holding_nature === "AS") && next.length < 2) next.push(emptyHolder("2"));
    next = next.map((holder, index) => ({
      ...holder,
      holder_rank: String(index + 1)
    }));
    if (nextProfile?.minor) next.push(emptyHolder("-1"));
    setHolders(next);
    setFatca(next.map(holder => {
      const existing = fatca.find(item => item.holder_rank === holder.holder_rank) || emptyFatca(holder.holder_rank);
      return {
        ...existing,
        holder_rank: holder.holder_rank,
        investor_type: nextProfile?.investorType || "",
        tax_status: nextProfile?.label || ""
      };
    }));
  };
  const changeTop = key => event => {
    const next = {
      ...top,
      [key]: event.target.value
    };
    setTop(next);
    setPreview(null);
    if (key === "tax_code" || key === "holding_nature") alignHolders(next);
    if (key === "is_client_demat" && event.target.value === "true" && !depositories.length) setDepositories([emptyDepository()]);
  };
  const toggleDematFatca = event => {
    const enabled = event.target.value === "true";
    setIncludeDematFatca(enabled);
    if (enabled) {
      setFatca(holders.map(holder => ({
        ...(fatca.find(item => item.holder_rank === holder.holder_rank) || emptyFatca(holder.holder_rank)),
        holder_rank: holder.holder_rank,
        investor_type: profile?.investorType || "",
        tax_status: profile?.label || ""
      })));
    }
  };
  const rawData = useMemo(() => {
    const taxStatus = profile?.label;
    const holderPayload = holders.map(holder => ({
      ...holder,
      nomination: holder.nomination.map(nominee => ({
        ...nominee,
        guardian: nominee.is_minor === "true" ? nominee.guardian : undefined
      }))
    }));
    const fatcaPayload = fatca.map(declaration => {
      const {
        ubo,
        npo,
        ...individualFields
      } = declaration;
      return nonIndividual ? {
        ...individualFields,
        ubo,
        npo: nonIndividual ? npo : undefined
      } : individualFields;
    });
    const data = {
      investor: {
        client_code: top.client_code.toUpperCase()
      },
      is_multi_ucc: top.is_multi_ucc,
      parent_client_code: top.parent_client_code,
      holding_nature: top.holding_nature,
      tax_status: taxStatus,
      tax_code: top.tax_code,
      rdmp_idcw_pay_mode: top.rdmp_idcw_pay_mode,
      is_client_physical: top.is_client_physical,
      is_client_demat: top.is_client_demat,
      is_nomination_opted: physical ? top.is_nomination_opted : undefined,
      nomination_auth_mode: physical ? top.nomination_auth_mode : undefined,
      nomination_approval: physical && ["W", "E"].includes(top.nomination_auth_mode) ? nominationApproval : undefined,
      nominee_soa: nominationEnabled ? top.nominee_soa : undefined,
      comm_mode: top.comm_mode,
      onboarding: top.onboarding,
      holder: holderPayload,
      comm_addr: profile?.resident ? commAddr : undefined,
      foreign_addr: profile && !profile.resident ? foreignAddr : undefined,
      depository: demat ? depositories : undefined,
      bank_account: banks,
      fatca: includeFatca ? fatcaPayload : undefined,
      pms_client: top.pms_client,
      pms_code: top.pms_client === "true" ? top.pms_code : undefined,
      identifiers: documents,
      aof: !physical || top.is_aof_submitted === "" ? undefined : {
        is_aof_submitted: top.is_aof_submitted
      },
      aof_ria: !physical || top.is_aof_ria_submitted === "" ? undefined : {
        is_aof_ria_submitted: top.is_aof_ria_submitted
      }
    };
    return clean(data);
  }, [top, profile, physical, demat, includeFatca, nominationEnabled, nonIndividual, nominationApproval, holders, commAddr, foreignAddr, depositories, banks, fatca, documents]);
  const serialized = JSON.stringify(rawData);
  const call = async dryRun => {
    if (!dryRun && serialized !== previewInput) return toast.error("The form changed after preview. Preview the exact wire payload again.");
    if (!dryRun && !window.confirm(`Submit UCC ${top.client_code.toUpperCase()} to BSE ${capability?.environment || "UAT"}?`)) return;
    setBusy(true);
    try {
      const {
        data
      } = await adminApi.post("/admin/bse2/ucc/register", {
        data: rawData,
        dry_run: dryRun
      });
      if (dryRun) {
        setPreview(data.payload);
        setPreviewInput(serialized);
        toast.success("v2.0.3 contract validated. Review the exact BSE payload.");
      } else if (data.status === "SUBMITTED") {
        toast.success(data.message || "UCC submitted to BSE");
        setPreview(null);
        setPreviewInput("");
      } else toast.error(data.message || `BSE returned ${data.status || "an error"}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return <AdminLayout><div className="space-y-5"><div className="flex flex-wrap items-end justify-between gap-3"><div><h1 className="font-heading text-3xl text-[var(--brand)]">Register Investor — BSE UCC v2.0.3</h1><p className="mt-1 max-w-3xl text-sm text-[var(--ink-muted)]">Every input below maps directly to an accepted <code>add_ucc</code> wire field. Member ID is server-owned. No DOB, mobile, address, bank, PAN, net worth, or FATCA value is fabricated.</p></div><div className="flex gap-2"><Badge variant="outline"><ShieldCheck className="mr-1 h-3.5 w-3.5" /> exact contract {contractMeta.version}</Badge>{contractMeta.memberId && <Badge variant="outline">member {contractMeta.memberId}</Badge>}</div></div><CapabilityNotice capability={capability} title="BSE UCC registration" />
<Card>
<CardHeader>
<CardTitle className="text-lg">Investor classification and modes</CardTitle></CardHeader>
<CardContent className="space-y-4">
<Section title="Exchange identity" note="member.member_id is injected from the authenticated backend configuration; operators cannot spoof it."><Grid><Field label="UCC / client code" value={top.client_code} minLength={2} maxLength={50} onChange={changeTop("client_code")} /><SelectField label="Tax code / investor type" value={top.tax_code} options={taxProfiles.map(item => ({
                value: item.code,
                label: `${item.code} — ${item.label} · ${item.investorType} · ${item.resident ? "resident" : "non-resident"}${item.minor ? " · minor" : ""}`
              }))} onChange={changeTop("tax_code")} hint="Uses the authoritative tax_status table. The conflicting portal tax_code numbering is not used." /><SelectField label="Holding nature" value={top.holding_nature} options={HOLDING} onChange={changeTop("holding_nature")} /><SelectField label="Redemption / IDCW payout mode" value={top.rdmp_idcw_pay_mode} options={IDCW} onChange={changeTop("rdmp_idcw_pay_mode")} /><SelectField label="Physical holding?" value={top.is_client_physical} options={boolOptions} onChange={changeTop("is_client_physical")} /><SelectField label="Demat holding?" value={top.is_client_demat} options={boolOptions} onChange={changeTop("is_client_demat")} /><SelectField label="Communication mode" value={top.comm_mode} options={COMM_MODE} onChange={changeTop("comm_mode")} /><SelectField label="Onboarding" value={top.onboarding} options={ONBOARDING} onChange={changeTop("onboarding")} /><SelectField label="Multiple-combination UCC?" value={top.is_multi_ucc} options={boolOptions} onChange={changeTop("is_multi_ucc")} />{top.is_multi_ucc === "true" && <Field label="Parent client code" value={top.parent_client_code} maxLength={50} onChange={changeTop("parent_client_code")} />}<SelectField label="PMS client?" value={top.pms_client} options={boolOptions} onChange={changeTop("pms_client")} />{top.pms_client === "true" && <Field label="PMS code" value={top.pms_code} maxLength={20} onChange={changeTop("pms_code")} />}
</Grid>
</Section>
{physical &&
<Section title="Physical-mode nomination controls" note="Live UAT requires nomination_auth_mode for a physical SI even when nomination is not opted."><Grid><SelectField label="Nomination opted?" value={top.is_nomination_opted} options={boolOptions} onChange={changeTop("is_nomination_opted")} /><SelectField label="Nomination authentication" value={top.nomination_auth_mode} options={NOM_AUTH} onChange={changeTop("nomination_auth_mode")} />{nominationEnabled && <SelectField label="Show nominee in statement of account?" value={top.nominee_soa} options={boolOptions} onChange={changeTop("nominee_soa")} />}
</Grid>
{["W", "E"].includes(top.nomination_auth_mode) && <div className="mt-3">
<DocumentFields label="Nomination approval" value={nominationApproval} types={[{
                value: top.nomination_auth_mode === "W" ? "wet_signature" : "aadhaar_esign",
                label: top.nomination_auth_mode === "W" ? "Wet signature" : "Aadhaar eSign"
              }]} onChange={setNominationApproval} /></div>}</Section>
}{profile &&
<Section title={profile.resident ? "Resident communication address" : "Foreign address"} note={profile.resident ? "Indian PIN derives city/state/country at BSE; address lines allow 120 characters." : "Mandatory for this non-resident tax code; address lines allow 40 characters and country is ISO-3."}>{profile.resident ? <AddressEditor value={commAddr} onChange={setCommAddr} /> : <AddressEditor foreign value={foreignAddr} onChange={setForeignAddr} />}</Section>
}
<Section title="Holders" note="Maximum 3 ordinary holders. Minor tax profiles also require guardian rank -1." action={holders.filter(item => item.holder_rank !== "-1").length < 3 && top.holding_nature !== "SI" ? <Button type="button" size="sm" variant="outline" onClick={() => {
            const rank = String(holders.filter(item => item.holder_rank !== "-1").length + 1);
            const next = [...holders.filter(item => item.holder_rank !== "-1"), emptyHolder(rank), ...holders.filter(item => item.holder_rank === "-1")];
            setHolders(next);
            if (includeFatca) setFatca([...fatca, emptyFatca(rank)]);
          }}><Plus className="mr-1 h-3.5 w-3.5" /> Holder</Button> : null}><div className="space-y-4">{holders.map((holder, index) => <HolderEditor key={`${holder.holder_rank}-${index}`} index={index} value={holder} nominationEnabled={nominationEnabled} onChange={next => updateAt(setHolders, index, () => next)} canRemove={holder.holder_rank !== "1" && holder.holder_rank !== "-1"} remove={() => {
                setHolders(holders.filter((_, position) => position !== index));
                setFatca(fatca.filter(item => item.holder_rank !== holder.holder_rank));
              }} />)}</div></Section>

<Section title="Bank accounts" note="One to five. Leading zeros are preserved. Live UAT enforces account_owner even for an adult." action={banks.length < 5 ? <Button type="button" size="sm" variant="outline" onClick={() => setBanks([...banks, emptyBank()])}><Plus className="mr-1 h-3.5 w-3.5" /> Bank</Button> : null}><div className="space-y-3">{banks.map((bank, index) => <div key={index} className="space-y-3 rounded-lg border p-3"><div className="flex justify-between"><p className="text-xs font-semibold">Bank {index + 1}</p>{banks.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setBanks(banks.filter((_, position) => position !== index))}><Trash2 className="h-3.5 w-3.5" /></Button>}</div><Grid><Field label="IFSC" value={bank.ifsc_code} maxLength={11} onChange={event => updateAt(setBanks, index, item => ({
                    ...item,
                    ifsc_code: event.target.value.toUpperCase()
                  }))} /><Field label="Account number" value={bank.bank_acc_num} minLength={9} maxLength={20} onChange={event => updateAt(setBanks, index, item => ({
                    ...item,
                    bank_acc_num: event.target.value
                  }))} /><SelectField label="Account type" value={bank.bank_acc_type} options={BANK_TYPE} onChange={event => updateAt(setBanks, index, item => ({
                    ...item,
                    bank_acc_type: event.target.value
                  }))} /><SelectField label="Account owner" value={bank.account_owner} options={OWNER} onChange={event => updateAt(setBanks, index, item => ({
                    ...item,
                    account_owner: event.target.value
                  }))} />
</Grid>
{bank.identifier.length ?
<DocumentFields label="Bank proof" value={bank.identifier[0]} types={BANK_DOCS} onChange={identifier => updateAt(setBanks, index, item => ({
                  ...item,
                  identifier: [identifier]
                }))} /> : <Button type="button" size="sm" variant="outline" onClick={() => updateAt(setBanks, index, item => ({
                  ...item,
                  identifier: [emptyIdentifier("cancel_cheque")]
                }))}><Plus className="mr-1 h-3.5 w-3.5" /> Bank proof</Button>}</div>)}
</div></Section>
{demat &&
<Section title="Depository accounts" note="One to five; linked bank account must exactly match one entered above. Portal v2.0.3 says CMBP is mandatory for CDSL, though its prose elsewhere conflicts." action={depositories.length < 5 ? <Button type="button" size="sm" variant="outline" onClick={() => setDepositories([...depositories, emptyDepository()])}><Plus className="mr-1 h-3.5 w-3.5" /> Depository</Button> : null}><div className="space-y-3">{depositories.map((dp, index) => <div key={index} className="rounded-lg border p-3"><Grid><SelectField label="Depository" value={dp.depository_code} options={[{
                    value: "CDSL",
                    label: "CDSL"
                  }, {
                    value: "NSDL",
                    label: "NSDL"
                  }]} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    depository_code: event.target.value
                  }))} /><Field label="DP ID — exact 8" value={dp.dp_id} maxLength={8} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    dp_id: event.target.value.toUpperCase()
                  }))} /><Field label="Client ID — exact 8" value={dp.client_id} maxLength={8} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    client_id: event.target.value
                  }))} /><Field label="CMBP ID — exact 8" value={dp.cmbp_id} maxLength={8} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    cmbp_id: event.target.value
                  }))} /><SelectField label="Linked bank account" value={dp.bank_account} options={banks.filter(item => item.bank_acc_num).map(item => ({
                    value: item.bank_acc_num,
                    label: item.bank_acc_num
                  }))} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    bank_account: event.target.value
                  }))} /><SelectField label="Account owner" value={dp.account_owner} options={OWNER} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    account_owner: event.target.value
                  }))} /><SelectField label="Default depository?" value={dp.is_default} options={boolOptions} onChange={event => updateAt(setDepositories, index, item => ({
                    ...item,
                    is_default: event.target.value
                  }))} /><div className="flex items-end"><Button type="button" variant="ghost" disabled={depositories.length === 1} onClick={() => setDepositories(depositories.filter((_, position) => position !== index))}><Trash2 className="mr-1 h-4 w-4" /> Remove</Button></div>
</Grid>
{dp.identifier.length ? <DocumentFields label="CML proof" value={dp.identifier[0]} types={[{ value: "cml_copy", label: "CML copy" }]} onChange={(identifier) => updateAt(setDepositories, index, (item) => ({ ...item, identifier: [identifier] }))} /> : <Button type="button" size="sm" variant="outline" onClick={() => updateAt(setDepositories, index, (item) => ({ ...item, identifier: [emptyIdentifier("cml_copy")] }))}><Plus className="mr-1 h-3.5 w-3.5" /> CML proof</Button>}
</div>)}
</div></Section>
}{demat && !physical && <Section title="Demat-only FATCA" note="FATCA is optional for a demat-only UCC in the published add_ucc table."><SelectField label="Include FATCA declarations?" value={String(includeDematFatca)} options={boolOptions} onChange={toggleDematFatca} /></Section>}{includeFatca &&
<Section title="FATCA / CRS" note="Published contract requires one declaration per holder for physical UCC. UAT may approve incomplete data but such a UCC is not transaction-ready."><div className="space-y-4">{fatca.map((item, index) => <FatcaEditor key={`${item.holder_rank}-${index}`} index={index} value={item} nonIndividual={nonIndividual} npoRequired={nonIndividual} onChange={next => updateAt(setFatca, index, () => next)} />)}</div></Section>
}
<Section title="Top-level mandatory documents" note="Allowed here: aof, fatca_form, aof_ria. This is the API identifiers[] block, not a browser-only attachment list." action={documents.length < 3 ? <Button type="button" size="sm" variant="outline" onClick={() => setDocuments([...documents, emptyIdentifier("")])}><Plus className="mr-1 h-3.5 w-3.5" /> Document</Button> : null}>
  {physical && <Grid cols="sm:grid-cols-2">
    <SelectField label="AOF submitted?" value={top.is_aof_submitted} options={boolOptions} onChange={changeTop("is_aof_submitted")} />
    <SelectField label="RIA AOF submitted?" value={top.is_aof_ria_submitted} options={boolOptions} onChange={changeTop("is_aof_ria_submitted")} />

</Grid>}

  <div className="mt-3 space-y-3">{documents.map((document, index) => <div key={index} className="relative rounded-lg border p-3">
<DocumentFields value={document} types={TOP_DOCUMENT_TYPES} onChange={next => updateAt(setDocuments, index, () => next)} />{documents.length > 1 && <Button type="button" size="sm" variant="ghost" className="absolute right-2 top-1" onClick={() => setDocuments(documents.filter((_, position) => position !== index))}><Trash2 className="h-3.5 w-3.5" /></Button>}</div>)}
</div>
</Section>
<div className="flex flex-wrap gap-2"><Button type="button" variant="outline" disabled={busy} onClick={() => call(true)}>Validate & preview exact wire JSON</Button><Button type="button" disabled={busy || !capability?.enabled || !preview || previewInput !== serialized} onClick={() => call(false)}><Send className="mr-2 h-4 w-4" />Submit validated payload to BSE</Button></div></CardContent></Card>
{preview &&
<Card>
<CardHeader>
<CardTitle className="text-lg">Exact request preview</CardTitle></CardHeader>
<CardContent><p className="mb-3 text-xs text-[var(--ink-muted)]">This is the body that will be passed to <code>POST /v2/add_ucc</code>. Any form change invalidates submission until previewed again.</p><Textarea readOnly className="min-h-[420px] font-mono text-xs" value={JSON.stringify(preview, null, 2)} /></CardContent></Card>
}</div></AdminLayout>;
}
